import type { ReactElement } from 'react'
import clsx from 'clsx'
import Link from 'next/link'
import { Card, Loading } from '@stuller/stullercom/ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { fas } from '@awesome.me/kit-3dbd93c064/icons'
import { CartItemFavoriteButton } from './CartItemFavoriteButton'
import { CartItemMoveFromButton } from './CartItemMoveFromButton'
import { CartItemRemoveButton } from './CartItemRemoveButton'
import { CartItemQuantityInput } from './CartItemQuantityInput'
import { CartItemInstructionsNotes } from './CartItemInstructionsNotes'
import { CartItemConfigurationSummary } from './cart-item-configuration/CartItemConfigurationSummary'
import { CartItemAlert } from './CartItemAlert'
import { CartItemDOTDescription } from './CartItemDOTDescription'
import { CartItemOptionCheckbox } from './CartItemOptionCheckbox'
import { CartItemShipStatus } from './CartItemShipStatus'
import type { CartLine } from '../shared/types'
import { CartItemInStockStatus } from './CartItemInStockStatus'
import { CartItemQuantityOnHand } from './CartItemQuantityOnHand'
import { CartItemDimensions } from './CartItemDimensions'
import { CartItemPrice } from './CartItemPrice'
import { CartItemSuccess } from './CartItemSuccess'
import { CartItemDOTOptions } from './CartItemDOTOptions'
import { CartItemReport } from './CartItemReport'
import { CartItemShipmentSelect } from './CartItemShipmentSelect'
import { useCartItemContext } from './context/useCartItemContext'
import { CartItemContextProvider } from './context/CartItemContextProvider'
import { CartItemCreationDate } from './CartItemCreationDate'

interface CartItemProps {
  /**
   * The cart line data
   */
  cartLine: CartLine
  /**
   * Disable input fields in the cart item
   */
  disableFormChanges?: boolean
  /**
   * The style of the cart item
   */
  style?: React.CSSProperties
}

interface CartItemInnerProps {
  /**
   * The style of the cart item
   */
  style?: React.CSSProperties
}

/**
 * The inner component that will show a single item in the cart
 */
function CartItemInner ({ style }: CartItemInnerProps): ReactElement {
  const {
    cartLine,
    loading,
    error,
    isConfiguration,
    imageUrl,
    itemTitle,
    itemUrl,
    itemNumber,
    canMatch,
    isMatched,
    canAnneal,
    isAnnealed,
    canOrderOnConsignment,
    isForConsignment,
    isNonReturnable,
    canEditItem
  } = useCartItemContext()
  const cardClassName = clsx(
    'p-4 mb-3',
    error.length > 0 ? 'border-danger border-3' : 'border-0'
  )

  return (
    <div style={style} className='p-0' data-test='saved-cart-item' id={`cart-item-${cartLine.id}`}>
      <Loading loading={loading}>
        <CartItemSuccess />
        <Card className={cardClassName}>
          <div className='row' data-test='saved-items-container'>
            {error.length > 0 && (
              <div className='mb-4'>
                <CartItemAlert type='danger' errors={error} />
              </div>
            )}
            <div className='d-block d-xl-none'>
              <CartItemInStockStatus inStockStatus={cartLine.inStockStatus} />
              <CartItemShipStatus shipDate={cartLine.shipDate} />
            </div>
            <div className='d-none d-xl-block col-auto'>
              <img src={imageUrl} alt={itemTitle} className='h-auto w-100' style={{ maxWidth: 160 }} />
            </div>
            <div className='col'>
              <div className='row'>
                <div className='col col-lg-auto d-none d-lg-block d-xl-none'>
                  <img src={imageUrl} alt={itemTitle} className='h-auto w-100 mb-3' style={{ maxWidth: 120 }} />
                </div>
                <div className='col'>
                  <img src={imageUrl} alt={itemTitle} className='d-block d-lg-none h-auto w-100 mb-3' style={{ maxWidth: 65 }} />
                  <CartItemShipmentSelect />
                  <div>
                    <Link href={itemUrl} data-test='item-number'>{itemNumber}</Link>
                    {canEditItem && (
                      <>
                        &nbsp;<span className='text-gray-300'>|</span>&nbsp;<Link href={itemUrl} data-test='edit-link'>Edit</Link>
                      </>
                    )}
                    <CartItemReport />
                  </div>
                  <p className='mb-0 mt-1 fs-5 lh-xs' data-test='item-description'>{itemTitle}</p>

                  <CartItemDimensions />
                  {isConfiguration && cartLine.item != null && (
                    <div className='d-none d-lg-block mt-3 d-print-none'>
                      <CartItemConfigurationSummary configurationId={cartLine.item.id} />
                    </div>
                  )}
                  <CartItemDOTDescription
                    description={cartLine.consultantDescription}
                    bagAndTagOptions={cartLine.options?.bagAndTagOptions}
                  />
                  <div className='d-print-none'>
                    {canMatch && (
                      <CartItemOptionCheckbox
                        label='Match for size & color'
                        value='isMatched'
                        checked={isMatched}
                        tooltip={(
                          <p className='mb-0'>
                            Bulk pricing is not available if this option is selected. The standard per-item price will apply instead.
                          </p>
                        )}
                        data-test='match-for-size-and-color-checkbox'
                      />
                    )}
                    {canAnneal && (
                      <CartItemOptionCheckbox
                        label='Anneal metal'
                        value='isAnnealed'
                        checked={isAnnealed}
                        tooltip={(
                          <p className='mb-0'>
                            <b>Anneal:</b> Eliminate the stresses created during mechanical working and restore the metal to maximum softness.
                          </p>
                        )}
                        data-test='anneal-metal-checkbox'
                      />
                    )}
                    {canOrderOnConsignment && (
                      <CartItemOptionCheckbox
                        label='Order for review'
                        value='isForConsignment'
                        checked={isForConsignment}
                        data-test='order-for-review-checkbox'
                      />
                    )}
                  </div>
                </div>
                <div className='col-5 col-sm-4'>
                  <div className='row'>
                    <div className='d-none d-xl-block'>
                      <CartItemInStockStatus inStockStatus={cartLine.inStockStatus} />
                      <CartItemShipStatus shipDate={cartLine.shipDate} />
                    </div>
                    <CartItemPrice
                      totalPrice={cartLine.price?.total}
                      quotePrice={cartLine.quotedPrice?.total}
                      discountedPrice={cartLine.price?.discount}
                      consultantDiscount={cartLine.options?.consultantDiscount}
                    />
                    {cartLine.options?.quantity != null && (
                      <CartItemQuantityInput
                        quantity={cartLine.options?.quantity}
                        soldByDescription={cartLine.item?.soldByDescription}
                      />
                    )}
                    <CartItemQuantityOnHand inStockStatus={cartLine.inStockStatus} />
                    {isNonReturnable && (
                      <div className='mt-2'>
                        <div className='d-flex justify-content-end'>
                          <div>
                            <FontAwesomeIcon icon={fas.faBan} className='me-2' size='lg' />
                            Non-Returnable
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {isConfiguration && cartLine.item != null && (
                <div className='d-block d-lg-none mt-4'>
                  <CartItemConfigurationSummary configurationId={cartLine.item.id} />
                </div>
              )}
              <CartItemInstructionsNotes
                specialInstructions={cartLine.options?.specialInstructions}
                customerNotes={cartLine.options?.customerNotes}
              />
              {cartLine.errors != null && cartLine.errors.length > 0 && (
                <div className='mt-4'>
                  <CartItemAlert type='danger' errors={cartLine.errors} />
                </div>
              )}
              {cartLine.notes != null && cartLine.notes.length > 0 && (
                <div className='mt-4'>
                  <CartItemAlert type='warning' notes={cartLine.notes} />
                </div>
              )}
            </div>
          </div>
          <CartItemDOTOptions />
          <div className='row mt-4 mt-xl-5 d-flex align-items-center d-print-none'>
            <div className='col-xl-auto text-end text-xl-start'>
              <CartItemCreationDate />
            </div>
            <div className='col justify-content-end'>
              <div className='d-flex justify-content-end'>
                <CartItemFavoriteButton />
                <CartItemMoveFromButton />
                <CartItemRemoveButton />
              </div>
            </div>
          </div>
        </Card>
      </Loading>
    </div>
  )
}

/**
 * The component that will show a single item in the cart
 */
function CartItem ({
  cartLine,
  disableFormChanges,
  style
}: CartItemProps): ReactElement {
  return (
    <CartItemContextProvider
      cartLine={cartLine}
      disableFormChanges={disableFormChanges}
    >
      <CartItemInner style={style} />
    </CartItemContextProvider>
  )
}

export {
  CartItem
}
